<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';

const dynamicWords = ['raw copy', 'document', 'word', 'pdf', 'idea'];
const currentWordIndex = ref(0);
let wordInterval: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  wordInterval = setInterval(() => {
    currentWordIndex.value = (currentWordIndex.value + 1) % dynamicWords.length;
  }, 2500);
});

onUnmounted(() => {
  if (wordInterval) {
    clearInterval(wordInterval);
  }
});
</script>

<template>
  <section class="relative overflow-hidden bg-transparent pt-24 md:pt-32">
    <div class="pointer-events-none absolute inset-0 overflow-hidden z-0">
      <div
        class="absolute left-1/2 top-0 h-[400px] w-[400px] -translate-x-1/2 rounded-full bg-black/5 blur-[80px]"
      />
    </div>

    <div class="relative z-10 mx-auto flex max-w-[1320px] flex-col px-4 pb-12 md:px-8 md:pb-24">
      <!-- Top Content -->
      <div class="mx-auto flex max-w-4xl flex-col items-center text-center">
        <h1
          class="mt-4 max-w-[1200px] font-boldonse text-[clamp(1.8rem,4.5vw,3.5rem)] leading-[1.2] text-black/90 uppercase tracking-tight w-full px-4"
        >
          <div class="flex flex-wrap items-center justify-center gap-x-3 md:gap-x-4">
            <span>Turn</span>
            <span
              class="inline-grid [grid-template-areas:'content'] text-center align-baseline text-[#fe713b]"
            >
              <Transition name="word-slide">
                <span
                  :key="dynamicWords[currentWordIndex]"
                  class="[grid-area:content] whitespace-nowrap"
                  >{{ dynamicWords[currentWordIndex] }}</span
                >
              </Transition>
              <!-- 占位符，撑开最大宽度防止闪烁和抖动 -->
              <span class="[grid-area:content] whitespace-nowrap invisible" aria-hidden="true"
                >document</span
              >
            </span>
            <span class="text-black/50">into</span>
          </div>
          <span
            class="text-black/50 block whitespace-nowrap overflow-visible text-[clamp(1.5rem,4vw,3.5rem)] tracking-tighter sm:tracking-tight w-full mt-2 md:mt-3"
            >production-ready designs</span
          >
        </h1>

        <p class="mt-5 max-w-2xl text-sm leading-relaxed text-black/60 md:text-lg">
          Works for social posts, decks, posters, LinkedIn/Meta ads, and EDM campaigns.
        </p>

        <div class="mt-8 flex w-full flex-col items-center justify-center gap-3 sm:flex-row">
          <NuxtLink
            to="/signup"
            class="inline-flex min-w-[160px] items-center justify-center rounded-full bg-black/90 px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-black hover:scale-105"
          >
            Start for Free
          </NuxtLink>
        </div>

        <p class="mt-4 text-[13px] text-black/40 font-medium">
          Get 300 free credits <span class="mx-1.5 text-black/20">|</span> No credit card required
        </p>
      </div>

      <!-- App Mockup UI -->
      <div
        class="mt-12 md:mt-16 overflow-hidden rounded-[32px] border border-black/10 bg-white/60 backdrop-blur-2xl shadow-[0_30px_80px_rgba(0,0,0,0.08)]"
      >
        <!-- Mockup Header -->
        <div
          class="flex items-center justify-between gap-3 border-b border-black/5 px-6 py-4 bg-white/40"
        >
          <div class="flex items-center gap-2">
            <span class="h-3 w-3 rounded-full bg-[#ff5f56] border border-black/10" />
            <span class="h-3 w-3 rounded-full bg-[#ffbd2e] border border-black/10" />
            <span class="h-3 w-3 rounded-full bg-[#27c93f] border border-black/10" />
          </div>
          <div
            class="rounded-full bg-black/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-black/40"
          >
            Veeso Workspace
          </div>
          <div class="w-[52px]"></div>
          <!-- Spacer for centering -->
        </div>

        <!-- Mockup Body -->
        <div
          class="p-6 md:p-10 bg-white/40 flex flex-col md:flex-row items-center justify-between gap-8 md:gap-12 relative"
        >
          <!-- Left: Simple Input -->
          <div class="w-full md:w-[40%] flex flex-col items-center justify-center space-y-6">
            <div class="text-center">
              <h3 class="text-xl font-semibold text-black/90">Drop your content</h3>
              <p class="mt-2 text-sm text-black/50">Raw copy, notes, or any document.</p>
            </div>

            <div
              class="w-full max-w-[320px] rounded-2xl border-2 border-dashed border-black/10 bg-white/50 p-6 md:p-8 flex flex-col items-center justify-center shadow-sm relative overflow-hidden group hover:border-[#fe713b]/50 transition-colors"
            >
              <div
                class="w-16 h-16 rounded-2xl bg-[#fe713b]/10 flex items-center justify-center text-[#fe713b] mb-4"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <line x1="10" y1="9" x2="8" y2="9" />
                </svg>
              </div>
              <div
                class="text-left w-full bg-white rounded-xl p-4 shadow-sm border border-black/5 text-[11px] text-black/60 font-medium leading-relaxed font-mono"
              >
                Exposição: O Sentido da Viagem <br />
                Desenhos de Julio Resende <br /><br />
                13 Janeiro - 25 Fevereiro <br />
                Casa Municipal da Cultura, Estarreja
              </div>
            </div>
          </div>

          <!-- Middle: Arrow / AI processing -->
          <div class="hidden md:flex flex-col items-center shrink-0 text-black/20">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
            <span
              class="mt-3 text-[10px] font-bold uppercase tracking-widest text-[#fe713b] bg-[#fe713b]/10 px-3 py-1 rounded-full"
              >Veeso AI</span
            >
          </div>

          <!-- Right: Stunning Output -->
          <div class="w-full md:w-[50%] flex flex-col items-center space-y-6">
            <div class="text-center">
              <h3 class="text-xl font-semibold text-black/90">Get polished designs</h3>
              <p class="mt-2 text-sm text-black/50">Fully editable, perfectly layouted.</p>
            </div>

            <div class="w-full relative">
              <!-- Floating badges -->
              <div class="absolute -top-3 -right-3 md:-right-6 z-10 flex flex-col gap-2 items-end">
                <span
                  class="rounded-full bg-black/90 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-white shadow-lg flex items-center gap-1.5"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                  </svg>
                  Fully editable
                </span>
                <span
                  class="rounded-full bg-white px-3 py-1.5 text-[10px] font-semibold text-black/60 shadow-md border border-black/5"
                >
                  Print-ready PDF & PNG
                </span>
              </div>

              <!-- Design Preview / Canvas Mockup -->
              <div
                class="rounded-3xl bg-[#f0f2f5] p-4 md:p-6 border border-black/5 shadow-inner relative flex items-center justify-center min-h-[360px]"
              >
                <!-- Canvas Grid Dots (Moved overflow-hidden here to not clip tooltips) -->
                <div class="absolute inset-0 z-0 overflow-hidden rounded-3xl">
                  <div
                    class="absolute inset-0 opacity-40"
                    style="
                      background-image: radial-gradient(#d1d5db 1px, transparent 1px);
                      background-size: 16px 16px;
                    "
                  ></div>
                </div>

                <!-- The "Selected" Canvas Object -->
                <div class="relative z-10 group cursor-default">
                  <div
                    class="overflow-hidden bg-white shadow-xl ring-1 ring-black/5 transition-transform hover:scale-[1.02] duration-300"
                  >
                    <img
                      src="https://static.veeso.ai/static-assets/card-bg/9-16/EventPost02.webp"
                      alt="Stunning editable design"
                      class="w-full h-auto object-cover max-w-[240px] md:max-w-[280px]"
                    />
                  </div>
                  <!-- Bounding Box / Selection Frame -->
                  <div
                    class="absolute -inset-2 border-2 border-[#fe713b] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-20"
                  >
                    <!-- Resize Handles -->
                    <div
                      class="absolute -top-1.5 -left-1.5 w-3 h-3 bg-white border-2 border-[#fe713b] rounded-sm"
                    ></div>
                    <div
                      class="absolute -top-1.5 -right-1.5 w-3 h-3 bg-white border-2 border-[#fe713b] rounded-sm"
                    ></div>
                    <div
                      class="absolute -bottom-1.5 -left-1.5 w-3 h-3 bg-white border-2 border-[#fe713b] rounded-sm"
                    ></div>
                    <div
                      class="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-white border-2 border-[#fe713b] rounded-sm"
                    ></div>
                    <!-- Context Menu Tooltip -->
                    <div
                      class="absolute -top-10 left-1/2 -translate-x-1/2 bg-[#2d3748] text-white text-[10px] font-medium px-3 py-1.5 rounded-md whitespace-nowrap shadow-lg flex items-center gap-2"
                    >
                      <span>A Text</span> <span class="w-px h-3 bg-white/20"></span>
                      <span>Image</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.word-slide-enter-active,
.word-slide-leave-active {
  transition: all 0.6s cubic-bezier(0.25, 1, 0.5, 1);
}

.word-slide-enter-from {
  opacity: 0;
  transform: translateY(60%);
}

.word-slide-leave-to {
  opacity: 0;
  transform: translateY(-60%);
}
</style>
